import {format, parse} from "numerable";
import NamaCommon from "@/utils/common/NamaCommon.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import DecimalScaleStrategies from "@/utils/constants/DecimalScaleStrategies.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import CommonFieldIds from "@/utils/constants/CommonFieldIds.ts";
import BSCEntities from "@/utils/constants/BSCEntities.ts";
import IdsAndConstants from "@/utils/UIConstants/IdsAndConstants.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import FieldMetaData = namaTypes.FieldMetaData;

export default class NumberUtils {
  static formatByPattern(specs: namaTypes.DTOFieldSpecs, value: number | undefined): string {
    if (!value)
      return "";
    return format(value, this.fetchPatternFromSpecs(specs));
  }

  private static fetchPatternFromSpecs(specs: namaTypes.DTOFieldSpecs) {
    let pattern = "0,0.X"
    if (specs.format) {
      pattern = specs.format;
      if (pattern.includes(",")) {
        if (pattern.includes(".")) {
          let beforeDot = pattern.substring(0, pattern.indexOf("."))
          let afterDot = pattern.substring(pattern.indexOf(".") + 1)
          pattern = "0,0." + afterDot;
        } else {
          pattern = "0,0"
        }
      }
    } else if (specs.decimalPlaces) {
      pattern = "0,0." + ("#".repeat(specs.decimalPlaces));
    }
    return pattern;
  }

  static parseFromPattern(specs: namaTypes.DTOFieldSpecs, value: string): number | null {
    return parse(value, {defaultPattern: this.fetchPatternFromSpecs(specs)});
  }

  static stripTrailingZeros(num: number): string {
    const numStr = num.toString();
    const [integerPart, decimalPart] = numStr.split('.');
    if (!decimalPart)
      return integerPart;
    const strippedDecimalPart = decimalPart.replace(/0+$/, '');
    if (strippedDecimalPart.length === 0)
      return integerPart;
    return `${integerPart}.${strippedDecimalPart}`;
  }

  static roundToDecimalPlaces(num: number, decimalPlaces: number): number {
    decimalPlaces = Math.max(0, Math.min(decimalPlaces, 20));
    const factor = Math.pow(10, decimalPlaces);
    return Math.round(num * factor) / factor;
  }

  static getDecimalScaleStrategyField(decimalScaleStrategy: string, object: Record<string, any>, fieldMetaData: FieldMetaData) {
    if (decimalScaleStrategy.startsWith("~")) {
      return NamaCommon.getFieldValue(object, decimalScaleStrategy.substring(1));
    }
    let newDecimalScaleStrategy: string = StringUtils.substringBeforeLast(fieldMetaData.fieldId!, ".");
    if (newDecimalScaleStrategy !== fieldMetaData.fieldId)
      decimalScaleStrategy = newDecimalScaleStrategy + "." + decimalScaleStrategy;
    return NamaCommon.getFieldValue(object, decimalScaleStrategy);
  }


  static calcFractionalDecimalPlaces(fieldMetaData: namaTypes.FieldMetaData, object: Record<string, any>): number | undefined {
    let fractionalDecimalPlaces: number | undefined;
    if (fieldMetaData.fieldType == FieldType.Decimal) {
      if (fieldMetaData.decimalScaleStrategy) {
        let decimalScaleStrategy: string = fieldMetaData.decimalScaleStrategy;
        if (ObjectChecker.areEqual(DecimalScaleStrategies.RATE, decimalScaleStrategy)) {
          fractionalDecimalPlaces = ClientCache.getRateScale();
        } else if (ObjectChecker.areEqual(DecimalScaleStrategies.PERCENT, decimalScaleStrategy)) {
          fractionalDecimalPlaces = ClientCache.getPercentageScale();
        } else if (ObjectChecker.areEqual(DecimalScaleStrategies.NONE, decimalScaleStrategy)) {
          fractionalDecimalPlaces = -1;
        } else {
          let decimalStrategyFieldValue = this.getDecimalScaleStrategyField(decimalScaleStrategy, object, fieldMetaData);
          if (typeof decimalStrategyFieldValue === 'object') {
            let entityType: string = decimalStrategyFieldValue[CommonFieldIds.ENTITY_TYPE];
            if (ObjectChecker.areEqual(entityType, BSCEntities.Currency)) {
              fractionalDecimalPlaces = NamaCommon.getFieldValue(decimalStrategyFieldValue, CommonFieldIds.EXTRA_DATA + "." + IdsAndConstants.displayDecimalPlaces);
            } else {
              fractionalDecimalPlaces = NamaCommon.getFieldValue(decimalStrategyFieldValue, CommonFieldIds.EXTRA_DATA + "." + IdsAndConstants.fractionDecimalPlaces);
            }
          } else if (typeof decimalStrategyFieldValue === 'number') {
            fractionalDecimalPlaces = decimalStrategyFieldValue;
          }
        }
      }
    }
    return fractionalDecimalPlaces;
  }

  static removeComma(value: string) {
    if (ObjectChecker.isString(value))
      value = value.replaceAll(",", "");
    return value;
  }
}
